import { jsx } from '@emotion/react';
import heroBackgroundImg from 'assets/images/shingles/hero.jpg';
import { Component, Fragment } from 'react';
import { Provider } from 'react-redux';

import FavoriteItem from './FavoriteItem';
import { connectFavorites } from './FavoritesConnectors';
import { connectRecentlyViewed } from './RecentlyViewedConnectors';
import useCrossTabShinglesFavoritesResync from './useCrossTabShinglesFavoritesResync';
import Breadcrumbs from '../../../Breadcrumbs';
import Conditional from '../../../Conditional';
import { withFeatureFlagsProvider } from '../../../FeatureFlag';
import FeatureFlagged from '../../../FeatureFlag/FeatureFlagged';
import HeroSubpage from '../../../HeroSubpage';
import { store } from '../../../Store';
import { OCNameFilter } from '../../../Utilities';
import { connectLocation } from '../../../location/LocationConnectors';
import LocationSwitcher from '../../../location/Switcher';
import BreadcrumbsContainer from '../BreadcrumbsContainer';
import ShingleRow from '../ShingleRow';
import { FeatureImage } from '../feature_utils';

const Favorites = ({ address, t, ...props }) => {
  const colors = props.shingles.flatMap(shingle => shingle.shingle_colors.map(c => ({ ...c, shingle })));
  const favorites = colors.filter(c => props.favorites.includes(c.uuid)).sort((a, b) => !!b.newcolor - !!a.newcolor);
  const recentlyViewed = colors
    .filter(c => props.recentlyViewed.includes(c.uuid))
    .sort((a, b) => !!b.newcolor - !!a.newcolor);
  const exploreMoreBg =
    'https://imagecdn.owenscorning.com/ocimages/image/upload/f_auto,w_270,q_70/v1584532418/roofing/shingles/favorites/explore-more-shingles.jpg';
  let favoritesRef = null;
  let recentlyViewedRef = null;

  useCrossTabShinglesFavoritesResync();

  const executeScroll = (refer, e) => {
    e.preventDefault();
    window.scrollTo({ top: refer.offsetTop + 10, behavior: 'smooth' });
  };
  return (
    <>
      <BreadcrumbsContainer>
        <Breadcrumbs
          items={[
            { text: t('shingles.breadcrumbs.roofing'), href: '/roofing' },
            { text: t('shingles.breadcrumbs.shingles'), href: '/roofing/shingles' },
          ]}
        />
        <LocationSwitcher showLocale postalRequired />
      </BreadcrumbsContainer>

      <HeroSubpage h1={t('shingles.favorites_title')} />

      <div className="bg-full-bleed oc-stickynav">
        <div className="stickynav-container">
          <FeatureFlagged except={['contractor_referral']}>
            <div className="cta-group">
              <a className="oc-cta-button" href="/roofing/contractors">
                {t('shingles.find_contractor')}
              </a>
            </div>
          </FeatureFlagged>

          <input type="checkbox" id="mobile-stickynav" />
          <label
            htmlFor="mobile-stickynav"
            className="mobile-stickynav-header"
            data-track="reveal"
            data-track-accordion="mobile-stickynav"
          >
            {t('owenscorning.components.sticky_nav.page_menu')}
          </label>

          <ul id="stickynav-slider" className="stickynav-links">
            <li>
              <a
                className="stickynav-anchor"
                href="#saved-shingles"
                onClick={e => executeScroll(favoritesRef, e)}
                data-track="tab-click"
                data-track-tab-name="Shingles"
              >
                {t('shingles.shingles')}
              </a>
            </li>
            <li>
              <a
                className="stickynav-anchor"
                href="#recently-viewed"
                onClick={e => executeScroll(recentlyViewedRef, e)}
                data-track="tab-click"
                data-track-tab-name="Recently Viewed"
              >
                {t('shingles.recently_viewed')}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <section id="saved-shingles" className="favorites" ref={ref => (favoritesRef = ref)}>
        <div className="favorite-header">
          <h2 className="h4 section-heading" role="status">
            {favorites.length} {t('shingles.saved_shingles')}
          </h2>
        </div>

        <div className="favorites-list">
          {favorites.map(f => (
            <FavoriteItem address={address} key={f.uuid} favorite={f} t={t} {...props} />
          ))}

          <a href="/roofing/shingles" className="add-favorite">
            <div className="add-favorite-container">
              <img src={exploreMoreBg} aria-hidden="true" />
              <div>
                <i className="fa fa-plus" aria-hidden="true" />
                {t('shingles.add_favorite_cta')}
              </div>
            </div>
          </a>
        </div>
      </section>

      <div className="bg-bleed bg-shaded with-section recently-viewed-box" ref={ref => (recentlyViewedRef = ref)}>
        <section id="recently-viewed" className="recently-viewed-shingles">
          <h2 className="h4 section-heading">{t('shingles.recently_viewed')}</h2>

          <Conditional if={!recentlyViewed}>
            <p>{t('shingles.no_views')}</p>

            <a
              href="/roofing/shingles"
              className="oc-cta-button"
              data-track="learn-more"
              data-track-element-location="main section CTA"
              data-track-destination="shingles"
            >
              {t('shingles.view_shingles')}
            </a>
          </Conditional>
          <Conditional if={recentlyViewed}>
            <ShingleRow recentlyViewed={recentlyViewed} shingle={false} address={address} />
          </Conditional>
        </section>
      </div>

      <FeatureFlagged except={['contractor_referral']}>
        <section className="find_contractor favorites-page">
          <div>
            <h2 className="h3">{t('shingles.find_contractor')}</h2>
            <p>{t('shingles.find_contractor_text')}</p>
            <a className="oc-cta-button" href="/roofing/contractors">
              {t('shingles.find_contractor')}
            </a>
          </div>
        </section>
      </FeatureFlagged>
    </>
  );
};

const ConnectedFavorites = connectLocation(connectFavorites(connectRecentlyViewed(Favorites)));

class FavoritesContainer extends Component {
  render() {
    return (
      <Provider store={store}>
        <ConnectedFavorites address={this.props.address} shingles={this.props.shingles} />
      </Provider>
    );
  }
}

export default withFeatureFlagsProvider(FavoritesContainer);
